import { useEffect, useMemo, useState } from 'react';
import { apiGet, apiPost, apiDelete, ApiError } from '../lib/apiClient';
import { useModal } from '../contexts/ModalContext';

/**
 * Section-by-section editor for one legal document, EN and ES side by side
 * (spec §6.5).
 *
 * ── Both languages at once, not a language switcher ──────────────────────────
 *
 * A revision is one agreement. An English change that has not reached Spanish
 * is a document in two states, which is the exact split this sub-project
 * exists to close — so the two columns sit next to each other and an admin
 * cannot forget the other half by navigating away from it.
 *
 * ── Generated sections are read-only, and say why ────────────────────────────
 *
 * A machine-owned section is regenerated from the plan catalog on every price
 * or feature change. Letting an admin type into one would take work they are
 * about to lose. The server rejects such an edit outright; this renders the
 * block disabled with a line naming what produces it, so the refusal is never
 * the first they hear of it.
 *
 * ── Nothing here publishes ───────────────────────────────────────────────────
 *
 * Saving writes a draft. Publishing is a separate, confirmed act that states
 * whether it re-prompts the user base — handled by the draft panel above,
 * because "I am writing" and "I am changing the agreement for everyone" should
 * not be one button.
 */

type Lang = 'en' | 'es';

interface Section {
  title: string;
  paragraphs?: string[];
  bullets?: string[];
  slot?: string;
}

interface DocumentContent {
  eyebrow?: string;
  title: string;
  description: string;
  sections: Section[];
}

type Content = Record<Lang, DocumentContent>;

interface Draft {
  id: string;
  reason: string;
  declaredMaterial?: boolean;
  content: Content;
}

const SLOT_SOURCE: Record<string, string> = {
  PLAN_TERMS: 'the tier prices and features in LynkPulse Plans & Pricing',
  DATA_PROCESSING: 'the data categories of every enabled feature',
};

/** Paragraphs and bullets edit as one textarea; blank lines separate entries. */
const toText = (values?: string[]) => (values ?? []).join('\n\n');
const fromText = (text: string) =>
  text
    .split(/\n\s*\n/)
    .map((v) => v.trim())
    .filter(Boolean);

export function LegalEditor({
  type,
  label,
  onChanged,
}: {
  type: string;
  label: string;
  onChanged?: () => void;
}) {
  const modal = useModal();
  const [open, setOpen] = useState(false);
  const [content, setContent] = useState<Content | null>(null);
  const [baseline, setBaseline] = useState<string>('');
  const [reason, setReason] = useState('');
  const [immaterial, setImmaterial] = useState(false);
  const [draftId, setDraftId] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [loaded, setLoaded] = useState(false);

  /**
   * Load the open draft if there is one, else the live document. Editing
   * always continues an existing draft rather than starting a competing one —
   * the server allows only one, and silently opening a second would discard
   * whichever was saved first.
   */
  useEffect(() => {
    if (!open || loaded) return;
    let cancelled = false;

    (async () => {
      setError(null);
      try {
        const draftRes = await apiGet<{ draft: Draft | null }>(`/admin/legal/${type}/draft`);
        if (cancelled) return;

        if (draftRes.draft) {
          setContent(draftRes.draft.content);
          setBaseline(JSON.stringify(draftRes.draft.content));
          setReason(draftRes.draft.reason);
          setImmaterial(draftRes.draft.declaredMaterial === false);
          setDraftId(draftRes.draft.id);
        } else {
          // The public read returns one language at a time, so both are
          // fetched — the editor is meaningless with only half the agreement.
          const [en, es] = await Promise.all([
            apiGet<{ content: DocumentContent }>(`/legal/${type}?lang=en`),
            apiGet<{ content: DocumentContent }>(`/legal/${type}?lang=es`),
          ]);
          if (cancelled) return;
          const live = { en: en.content, es: es.content };
          setContent(live);
          setBaseline(JSON.stringify(live));
        }
        setLoaded(true);
      } catch (err) {
        if (!cancelled) setError(err instanceof ApiError ? err.message : 'Could not load the document');
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [open, loaded, type]);

  const dirty = useMemo(
    () => content !== null && JSON.stringify(content) !== baseline,
    [content, baseline],
  );

  const edit = (lang: Lang, index: number, patch: Partial<Section>) => {
    setContent((prev) => {
      if (!prev) return prev;
      const sections = prev[lang].sections.map((s, i) => (i === index ? { ...s, ...patch } : s));
      return { ...prev, [lang]: { ...prev[lang], sections } };
    });
  };

  const save = async () => {
    if (!content) return;
    if (!reason.trim()) {
      setError('A reason is required — it is what the revision history shows.');
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const draft = await apiPost<Draft>(`/admin/legal/${type}/draft`, {
        content,
        reason: reason.trim(),
        declaredMaterial: !immaterial,
      });
      setDraftId(draft.id);
      setBaseline(JSON.stringify(content));
      onChanged?.();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not save the draft');
    } finally {
      setBusy(false);
    }
  };

  const discard = async () => {
    const confirmed = await modal.confirm({
      title: 'Discard this draft?',
      message: 'The unpublished edits are deleted. The live document is untouched.',
      confirmLabel: 'Discard draft',
      danger: true,
    });
    if (!confirmed) return;

    setBusy(true);
    setError(null);
    try {
      await apiDelete(`/admin/legal/${type}/draft`);
      setDraftId(null);
      setLoaded(false);
      setContent(null);
      setReason('');
      setImmaterial(false);
      onChanged?.();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not discard the draft');
    } finally {
      setBusy(false);
    }
  };

  if (!open) {
    return (
      <div className="plan-card-footer">
        <button type="button" className="btn" onClick={() => setOpen(true)}>
          Edit {label}
        </button>
      </div>
    );
  }

  return (
    <div className="card">
      <div className="card-header">
        <h3 className="card-title">
          Editing {label}
          {draftId ? ' — draft open' : ''}
        </h3>
      </div>
      <div className="card-body">
        {error && (
          <div role="alert" className="error-banner">
            {error}
          </div>
        )}
        {!content && !error && <p className="cell-sub">Loading…</p>}

        {content && (
          <>
            <div className="form-group form-group-default">
              <label htmlFor={`reason-${type}`}>Reason for this revision</label>
              <input
                id={`reason-${type}`}
                className="form-control"
                value={reason}
                placeholder="Clarified the refund window in §9"
                onChange={(e) => setReason(e.target.value)}
              />
            </div>

            <label className="cell-sub" style={{ display: 'block', margin: '0.5rem 0 1rem' }}>
              <input
                type="checkbox"
                checked={immaterial}
                onChange={(e) => setImmaterial(e.target.checked)}
                style={{ marginRight: '0.5rem' }}
              />
              {/* Default is unchecked — over-prompting is an annoyance, failing
                  to prompt is a consent problem. */}
              This is a typo or wording fix that changes nothing a user agreed to (nobody will be
              asked to re-accept)
            </label>

            {content.en.sections.map((section, index) => {
              const es = content.es.sections[index];
              const generated = Boolean(section.slot);
              return (
                <div key={`${section.title}-${index}`} className="legal-section-row">
                  <div className="legal-section-head">
                    <strong>{section.title}</strong>
                    {generated && (
                      <span className="cell-sub" data-testid={`generated-note-${index}`}>
                        {' '}
                        · generated from {SLOT_SOURCE[section.slot!] ?? 'the plan catalog'} — edit
                        the plan, not this text
                      </span>
                    )}
                  </div>

                  <div className="legal-section-langs">
                    {(['en', 'es'] as Lang[]).map((lang) => {
                      const s = lang === 'en' ? section : es;
                      if (!s) return null;
                      return (
                        <div key={lang} className="form-group form-group-default">
                          <label htmlFor={`${type}-${lang}-${index}`}>
                            {lang === 'en' ? 'English' : 'Español'}
                          </label>
                          <textarea
                            id={`${type}-${lang}-${index}`}
                            className="form-control"
                            rows={5}
                            disabled={generated}
                            readOnly={generated}
                            aria-readonly={generated}
                            value={toText(s.paragraphs?.length ? s.paragraphs : s.bullets)}
                            onChange={(e) =>
                              edit(
                                lang,
                                index,
                                s.paragraphs?.length
                                  ? { paragraphs: fromText(e.target.value) }
                                  : { bullets: fromText(e.target.value) },
                              )
                            }
                          />
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            })}

            <div className="plan-card-footer">
              {dirty && <span className="cell-sub">Unsaved changes</span>}
              {draftId && (
                <button type="button" className="btn" onClick={discard} disabled={busy}>
                  Discard draft
                </button>
              )}
              <button type="button" className="btn" onClick={() => setOpen(false)} disabled={busy}>
                Close
              </button>
              <button
                type="button"
                className="btn btn-primary"
                onClick={save}
                disabled={busy || !dirty}
              >
                {busy ? 'Saving…' : 'Save draft'}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
